import { registerSessionCache } from './session-caches';
/**
 * 대시보드 위젯 공용 데이터 캐시 — TTL + single-flight.
 *
 * 배경(성능/병목):
 *   위젯 보드는 한 화면에 10+ 위젯이 동시에 마운트된다. 각 위젯이 기존 방식대로
 *   `useEffect` 에서 제 API 를 직접 부르면 최초 진입 1회에 수십 건의 요청이 몰리고,
 *   탭(보드) 전환마다 그 버스트가 반복된다. 운영에는 per-IP 분당 요청 상한이 있어
 *   이 버스트가 429 를 유발한 전례가 있다(강력 새로고침 로그아웃 사건).
 *
 * 본 모듈이 막는 것 2가지:
 *   1) **중복 요청(single-flight)** — 같은 key 의 요청이 이미 비행 중이면 새로 쏘지 않고
 *      그 Promise 에 합류한다. 같은 데이터를 쓰는 위젯이 여러 개여도 네트워크는 1회.
 *      (예: 캘린더 위젯 + 일정 상세 위젯이 같은 '오늘 일정'을 본다)
 *   2) **재요청(TTL)** — 보드 탭을 오가거나 위젯을 재배치해 리마운트돼도 TTL 안이면
 *      메모리에서 즉시 돌려준다. 로딩 스피너 깜빡임도 함께 사라진다.
 *
 * 실패는 캐시하지 않는다 — 다음 마운트에서 정상 재시도된다.
 * 모듈 레벨 Map 이라 라우트 전환에도 살아남고, 새로고침 시 자연 소멸한다.
 */

interface CacheEntry {
  at: number;
  data: unknown;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

/** 기본 TTL — 대시보드 위젯 정도의 신선도(1분). */
export const WIDGET_CACHE_TTL_MS = 60_000;

/** TTL 안의 캐시 값을 동기로 훔쳐본다. 없으면 undefined. 초기 렌더 시드용. */
export function peekWidgetCache<T>(key: string, ttlMs: number = WIDGET_CACHE_TTL_MS): T | undefined {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return hit.data as T;
  return undefined;
}

/**
 * 캐시·중복합류를 거쳐 loader 를 실행한다.
 * 같은 key 로 동시에 불리면 loader 는 한 번만 실행된다.
 */
export function widgetFetch<T>(
  key: string,
  loader: () => Promise<T>,
  ttlMs: number = WIDGET_CACHE_TTL_MS,
): Promise<T> {
  const hit = peekWidgetCache<T>(key, ttlMs);
  if (hit !== undefined) return Promise.resolve(hit);

  const running = inflight.get(key);
  if (running) return running as Promise<T>;

  const p = loader()
    .then((data) => {
      cache.set(key, { at: Date.now(), data });
      inflight.delete(key);
      return data;
    })
    .catch((err: unknown) => {
      // 실패는 캐시하지 않는다 — 다음 호출에서 다시 시도한다.
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, p);
  return p;
}

/**
 * 캐시 무효화. key 생략 시 전체.
 * prefix 로 끝에 '*' 를 주면 접두어 일치 전부 (예: `invalidateWidgetCache('hr:*')`).
 */
export function invalidateWidgetCache(key?: string): void {
  if (key === undefined) {
    cache.clear();
    return;
  }
  if (key.endsWith('*')) {
    const prefix = key.slice(0, -1);
    for (const k of [...cache.keys()]) {
      if (k.startsWith(prefix)) cache.delete(k);
    }
    return;
  }
  cache.delete(key);
}

/** 테스트 전용 — 캐시와 비행 중 요청을 모두 비운다. */
export function __resetWidgetCache(): void {
  cache.clear();
  inflight.clear();
}

// ★ 로그아웃하면 비운다 (2026-08-31) — 위젯에 담긴 것은 **그 사람의** 숫자다.
registerSessionCache(() => { cache.clear(); inflight.clear(); });
